import { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import cloneDeep from 'lodash/cloneDeep';

import { QUESTION_TYPES } from '@api/instant_survey';
import { FlowArrowIcon, MinusPlusButtons, Pill, TargetIcon } from '@utilities';

import DisqualifyLogicRow from './components/DisqualifyLogicRow';
import QuestionLogicOptions from './components/QuestionLogicOptions';
import SkipLogicRow from './components/SkipLogicRow';
import { answerOperators, disqualifyOperators, skipOperators } from './utilities/constants';
import { getQuestionLogicSourceQuestions } from '../../../../utilities/helpers';

import styles from './_index.module.scss';

const QuestionLogic = ({
  firstErrorRef,
  isDetailedQuestionView,
  isMultiLevelDisqualifyEnabled = true,
  isReadOnly,
  question,
  questionLogic,
  selectedQuestions = [],
  setQuestionLogic,
  type,
}) => {
  const addLogic = () => {
    const updatedLogic = cloneDeep(questionLogic);
    if (type === 'disqualify') {
      if (!isMultiLevelDisqualifyEnabled) return;

      updatedLogic[question.id].disqualify.logic = [
        ...updatedLogic[question.id].disqualify.logic,
        {
          answers: [],
          hasAnswerError: false,
          isFirstAnswerError: false,
          operator: answerOperators[0],
        },
      ];
    } else {
      updatedLogic[question.id].skip.logic = [
        ...updatedLogic[question.id].skip.logic,
        {
          answers: [],
          operator: answerOperators[0],
          question: {},
        },
      ];
    }
    setQuestionLogic(updatedLogic);
  };

  const getRemovalIndex = (logicRows, index) =>
    Number.isInteger(index) ? index : logicRows.length - 1;

  const removeLogic = (index) => {
    const updatedLogic = cloneDeep(questionLogic);
    if (type === 'disqualify') {
      if (!isMultiLevelDisqualifyEnabled) {
        delete updatedLogic[question.id].disqualify;
        setQuestionLogic(updatedLogic);
        return;
      }

      if (updatedLogic[question.id].disqualify.logic.length === 1) {
        delete updatedLogic[question.id].disqualify;
      } else {
        const removalIndex = getRemovalIndex(updatedLogic[question.id].disqualify.logic, index);
        updatedLogic[question.id].disqualify.logic.splice(removalIndex, 1);
      }
    } else {
      if (updatedLogic[question.id].skip.logic.length === 1) {
        delete updatedLogic[question.id].skip;
      } else {
        const removalIndex = getRemovalIndex(updatedLogic[question.id].skip.logic, index);
        updatedLogic[question.id].skip.logic.splice(removalIndex, 1);
      }
    }
    setQuestionLogic(updatedLogic);
  };

  const renderLogicIcon = () => {
    switch (type) {
      case 'disqualify':
        return <TargetIcon />;
      case 'skip':
        return <FlowArrowIcon />;
      default:
        return;
    }
  };

  const updateOperator = (operator) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[question.id][type].operator = operator;
    setQuestionLogic(updatedLogic);
  };

  const updateDisqualifyAnswers = (answers) => {
    const updatedLogic = cloneDeep(questionLogic);
    updatedLogic[question.id].disqualify.answers = answers;
    updatedLogic[question.id].disqualify.hasAnswerError = false;
    updatedLogic[question.id].disqualify.isFirstAnswerError = false;
    setQuestionLogic(updatedLogic);
  };
  const isInstructionalText = question.type === QUESTION_TYPES.INSTRUCTIONS.value;
  const questionPillLogic =
    type === 'disqualify'
      ? questionLogic[question.id].disqualify?.operator?.value === 'ANY'
        ? 'OR'
        : 'AND'
      : questionLogic[question.id].skip?.operator?.value === 'ANY'
        ? 'OR'
        : 'AND';
  const questionLogicSourceQuestions = getQuestionLogicSourceQuestions({
    currentQuestion: question,
    questions: selectedQuestions,
  });

  return (
    <>
      {isDetailedQuestionView && type === 'disqualify' && (
        <div className={styles['question-logic-line']} />
      )}
      <div
        className={classNames(styles['question-logic'], {
          [styles['is-compact-view']]: !isDetailedQuestionView,
        })}
      >
        <span className={styles['question-logic-text']}>
          {renderLogicIcon()}
          {!isDetailedQuestionView && (
            <span>
              This question has&nbsp;
              <strong>{type === 'disqualify' ? 'disqualification logic' : 'question logic'}</strong>
            </span>
          )}
          {isDetailedQuestionView && (
            <>
              <div>
                {type === 'disqualify' && (
                  <>
                    {isMultiLevelDisqualifyEnabled ? (
                      <>
                        <span>Disqualify if&nbsp;</span>
                        <QuestionLogicOptions
                          isCompleted={isReadOnly}
                          options={disqualifyOperators}
                          optionsLabel="Operator"
                          optionsType="radio"
                          setValue={updateOperator}
                          value={questionLogic[question.id].disqualify.operator}
                        />
                        <span>&nbsp;apply</span>
                      </>
                    ) : (
                      <>
                        <span>Disqualify if the answer is&nbsp;</span>
                        <QuestionLogicOptions
                          isCompleted={isReadOnly}
                          options={answerOperators}
                          optionsLabel="Operator"
                          optionsType="radio"
                          setValue={updateOperator}
                          value={questionLogic[question.id].disqualify.operator}
                        />
                        <span>:&nbsp;</span>
                        <QuestionLogicOptions
                          firstErrorRef={firstErrorRef}
                          isCompleted={isReadOnly}
                          isError={questionLogic[question.id].disqualify.hasAnswerError}
                          isFocused={questionLogic[question.id].disqualify.isFirstAnswerError}
                          isRequired
                          options={question.answers}
                          optionsLabel="Answer Options"
                          optionsType="checkbox"
                          setValue={updateDisqualifyAnswers}
                          value={questionLogic[question.id].disqualify.answers}
                        />
                      </>
                    )}
                  </>
                )}
                {type === 'skip' && (
                  <>
                    <span>
                      {isInstructionalText ? 'Show this text if' : 'Ask this question if'}&nbsp;
                    </span>
                    <QuestionLogicOptions
                      isCompleted={isReadOnly}
                      options={skipOperators}
                      optionsLabel="Operator"
                      optionsType="radio"
                      setValue={updateOperator}
                      value={questionLogic[question.id].skip.operator}
                    />
                    <span>&nbsp;apply:</span>
                  </>
                )}
              </div>
            </>
          )}
        </span>
        {isDetailedQuestionView && !isReadOnly && (
          <span className={styles['question-logic-minus-plus-buttons']}>
            <MinusPlusButtons
              disablePlusButton={
                type === 'disqualify'
                  ? questionLogic[question.id].disqualify?.logic?.length >= 3
                  : questionLogic[question.id].skip?.logic.length >= 3
              }
              hasPlusButton={type !== 'disqualify' || isMultiLevelDisqualifyEnabled}
              isNeutral
              onClickMinus={removeLogic}
              onClickPlus={addLogic}
            />
          </span>
        )}
      </div>
      {isDetailedQuestionView && type === 'disqualify' && isMultiLevelDisqualifyEnabled && (
        <>
          {questionLogic[question.id].disqualify.logic.map((logic, index) => (
            <Fragment key={`${question.id}-disqualify-${index}`}>
              <div className={styles['question-logic-line']} />
              {index !== 0 && (
                <>
                  <Pill
                    className={styles['question-logic-pill']}
                    isDisabled
                    value={questionPillLogic}
                  />
                  <div className={styles['question-logic-line']} />
                </>
              )}
              <div className={styles['question-logic']}>
                <span className={styles['question-logic-text']}>
                  <DisqualifyLogicRow
                    firstErrorRef={firstErrorRef}
                    index={index}
                    isCompleted={isReadOnly}
                    question={question}
                    questionId={question.id}
                    questionLogic={questionLogic}
                    setQuestionLogic={setQuestionLogic}
                  />
                </span>
                {!isReadOnly && (
                  <span className={styles['question-logic-minus-plus-buttons']}>
                    <MinusPlusButtons
                      hasPlusButton={false}
                      isNeutral
                      onClickMinus={() => removeLogic(index)}
                    />
                  </span>
                )}
              </div>
            </Fragment>
          ))}
          <div className={styles['question-logic-line']} />
        </>
      )}
      {isDetailedQuestionView && type === 'skip' && (
        <>
          {questionLogic[question.id].skip.logic.map((logic, index) => {
            return (
              <Fragment key={`${question.id}-skip-${index}`}>
                <div className={styles['question-logic-line']} />
                {index !== 0 && (
                  <>
                    <Pill
                      className={styles['question-logic-pill']}
                      isDisabled
                      value={questionPillLogic}
                    />
                    <div className={styles['question-logic-line']} />
                  </>
                )}
                <div className={styles['question-logic']}>
                  <span className={styles['question-logic-text']}>
                    <SkipLogicRow
                      firstErrorRef={firstErrorRef}
                      index={index}
                      isCompleted={isReadOnly}
                      questionId={question.id}
                      questionIndex={questionLogicSourceQuestions.length}
                      questionList={questionLogicSourceQuestions}
                      questionLogic={questionLogic}
                      setQuestionLogic={setQuestionLogic}
                    />
                  </span>
                  {!isReadOnly && (
                    <span className={styles['question-logic-minus-plus-buttons']}>
                      <MinusPlusButtons
                        hasPlusButton={false}
                        isNeutral
                        onClickMinus={() => removeLogic(index)}
                      />
                    </span>
                  )}
                </div>
              </Fragment>
            );
          })}
          <div className={styles['question-logic-line']} />
        </>
      )}
    </>
  );
};

QuestionLogic.propTypes = {
  firstErrorRef: PropTypes.object.isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  isMultiLevelDisqualifyEnabled: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  question: PropTypes.object.isRequired,
  questionLogic: PropTypes.object.isRequired,
  selectedQuestions: PropTypes.array,
  setQuestionLogic: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['disqualify', 'skip']).isRequired,
};

export default QuestionLogic;
